import React, { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin, ExternalLink, User } from "lucide-react";

const BRANCHES = [
  {
    city: "Frankfurt",
    role: "Hauptsitz",
    address: "Berner Straße 79, 60437 Frankfurt am Main",
    email: "sales.fra@xortec.de",
    tel: "+496950698860",
    telDisplay: "069 5069886-0",
    contact: "Michael Kahl",
  },
  {
    city: "Berlin",
    role: "Niederlassung",
    address: "Am Wall 22, 14979 Großbeeren",
    email: "vertrieb@xortec.de",
    tel: "+493370174700",
    telDisplay: "033701 7470-0",
    contact: "Thoralf Brandt",
  },
  {
    city: "Hamburg",
    role: "Niederlassung",
    address: "Heidenkampsweg 58, 20097 Hamburg",
    email: "vertrieb@xortec.de",
    tel: "+4940547240",
    telDisplay: "040 547240-0",
    contact: "Dirk Tscharnesk",
  },
  {
    city: "München",
    role: "Niederlassung",
    address: "Konrad-Zuse-Platz 8, 81829 München",
    email: "vertrieb@xortec.de",
    tel: "+498982988200",
    telDisplay: "089 829882-0",
    contact: "Markus Küffner",
  },
  {
    city: "Regensburg",
    role: "Niederlassung",
    address: "Hermann-Köhl-Str. 2a, 93049 Regensburg",
    email: "vertrieb@xortec.de",
    tel: "+498982988200",
    telDisplay: "089 829882-0",
    contact: "Markus Küffner",
  },
];

function buildMailto({ email, subject, body }) {
  const p = new URLSearchParams();
  if (subject) p.set("subject", subject);
  if (body) p.set("body", body);
  const qs = p.toString().replace(/\+/g, "%20");
  return `mailto:${email}?${qs}`;
}

export default function ContactDialog({ open, onOpenChange, context }) {
  const { subject, body } = useMemo(() => {
    if (!context) {
      return {
        subject: "Anfrage – Milestone XProtect",
        body: "Guten Tag,\n\nich habe eine Frage zu Milestone XProtect.\n\nMit freundlichen Grüßen",
      };
    }
    const { projectName, recommendedEditionLabel, scores, answers, items, configCode, mode } = context;
    if (mode === "configurator" && Array.isArray(items)) {
      const lines = [
        "Guten Tag,",
        "",
        "über den XProtect Konfigurator habe ich folgende Lizenz-Zusammenstellung erstellt:",
        "",
        `Projekt: ${projectName || "—"}`,
      ];
      if (configCode) lines.push(`Konfigurationscode: ${configCode}`);
      lines.push("", "Positionen:");
      for (const it of items) {
        lines.push(`  • ${it.qty}× ${it.name} (Xortec-Nr. ${it.xortecNo}, ${it.manufacturerNo})`);
      }
      lines.push("", "Bitte senden Sie mir ein Angebot zu dieser Konfiguration.", "", "Mit freundlichen Grüßen");
      return {
        subject: `Angebotsanfrage XProtect Konfiguration – ${projectName || "neues Projekt"}`,
        body: lines.join("\n"),
      };
    }
    const lines = [
      "Guten Tag,",
      "",
      "über den XProtect Auswahlassistenten habe ich folgende Empfehlung erhalten:",
      "",
      `Projekt: ${projectName || "—"}`,
      `Empfohlene Edition: ${recommendedEditionLabel || "—"}`,
    ];
    if (scores) {
      const parts = Object.entries(scores).map(
        ([k, v]) => `  • ${k}: ${v}%`
      );
      lines.push("Eignung je Edition:", ...parts);
    }
    if (answers) {
      lines.push("", "Angaben:");
      const readable = {
        cameras: "Kameras",
        sites: "Standorte",
        recordingServers: "Recording Server",
        failover: "Failover",
        smartWall: "Smart Wall",
        smartMap: "Smart Map",
        federatedArchitecture: "Federation",
        evidenceLock: "Evidence Lock",
        secureEvidenceHandling: "Secure Evidence Handling",
        incidentManager: "Incident Manager",
        dashboard: "Dashboard",
        deviceManagement: "Geräteverwaltung",
        scalability: "Skalierbarkeit",
        criticality: "Kritikalität",
      };
      for (const [k, v] of Object.entries(answers)) {
        if (v === null || v === undefined || v === "" || (typeof v === "object" && Object.keys(v || {}).length === 0)) continue;
        const label = readable[k] || k;
        let disp = v;
        if (typeof v === "boolean") disp = v ? "Ja" : "Nein";
        else if (typeof v === "object") disp = Object.entries(v).filter(([, val]) => val).map(([kk]) => kk).join(", ") || "—";
        lines.push(`  • ${label}: ${disp}`);
      }
    }
    lines.push("", "Bitte kontaktieren Sie mich für ein individuelles Angebot.", "", "Mit freundlichen Grüßen");
    return {
      subject: `Angebotsanfrage XProtect – ${projectName || "neues Projekt"}`,
      body: lines.join("\n"),
    };
  }, [context]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="contact-dialog" className="max-w-3xl bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-xl">Kontakt aufnehmen</DialogTitle>
          <DialogDescription>
            Wählen Sie die für Sie nächstgelegene Xortec-Niederlassung. Bei Nutzung aus dem Ergebnis-Bereich werden Ihre Angaben
            und die Empfehlung automatisch in die E-Mail übernommen.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          {BRANCHES.map((b) => (
            <div
              key={b.city}
              data-testid={`contact-branch-${b.city.toLowerCase()}`}
              className="rounded-lg border border-border bg-secondary/30 p-4 tile-hover"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">{b.role}</div>
                  <div className="font-semibold text-lg leading-tight">{b.city}</div>
                </div>
                <MapPin className="w-4 h-4 text-primary" />
              </div>
              <div className="text-xs text-muted-foreground mb-1">{b.address}</div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                <User className="w-3 h-3" />
                <span>{b.contact}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  asChild
                  size="sm"
                  className="bg-primary text-primary-foreground"
                  data-testid={`contact-mail-${b.city.toLowerCase()}`}
                >
                  <a href={buildMailto({ email: b.email, subject, body })}>
                    <Mail className="w-3.5 h-3.5 mr-1.5" /> E-Mail
                  </a>
                </Button>
                <Button asChild size="sm" variant="outline">
                  <a href={`tel:${b.tel}`}>
                    <Phone className="w-3.5 h-3.5 mr-1.5" /> {b.telDisplay}
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-border pt-4 mt-2">
          <span className="text-xs text-muted-foreground">Sie erreichen uns Montag bis Freitag von 8–17 Uhr.</span>
          <Button asChild variant="ghost" size="sm">
            <a href="https://xortec.de/Support/Kontakt/" target="_blank" rel="noreferrer">
              Alle Kontaktdaten <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
